/**
 * Development Service
 *
 * Service for fetching development summary and project status information
 * to help developers re-engage with the project.
 */

import { BaseService } from './BaseService';

export interface ProjectStatus {
  project_name: string;
  version: string;
  environment: string;
  backend_status: {
    status: string;
    message: string;
  };
  frontend_status: {
    status: string;
    message: string;
  };
  database_status: {
    status: string;
    message: string;
  };
  last_updated: string;
}

export interface ImplementationPlanSummary {
  available: boolean;
  last_modified?: string;
  current_phase?: string;
  next_priority_items?: string[];
  recent_progress?: string[];
  immediate_issues?: string[];
  completion_status?: {
    completed_items: number;
    pending_items: number;
    total_items: number;
    completion_percentage: number;
  };
  error?: string;
}

export interface DevelopmentSummary {
  project_status: ProjectStatus;
  implementation_plan: ImplementationPlanSummary;
  quick_links: {
    backend_api: string;
    frontend_app: string;
    api_docs: string;
    implementation_plan: string;
  };
  development_tips: string[];
}

class DevelopmentService extends BaseService {
  constructor() {
    super();
  }

  /**
   * Get comprehensive development summary
   */
  async getDevelopmentSummary(): Promise<DevelopmentSummary> {
    return DevelopmentService.get<DevelopmentSummary>('/dev/summary');
  }

  /**
   * Get project status
   */
  async getProjectStatus(): Promise<ProjectStatus> {
    return DevelopmentService.get<ProjectStatus>('/dev/status');
  }

  /**
   * Get implementation plan summary
   */
  async getImplementationPlanSummary(): Promise<ImplementationPlanSummary> {
    return DevelopmentService.get<ImplementationPlanSummary>('/dev/implementation-plan');
  }
}

export const developmentService = new DevelopmentService();
